//
//  useApi.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    Projects service responses and supplies cached or strict refresh operations.
//

import { type IReturn } from "@servicestack/client";
import { useContext, useMemo } from "react";
import { type KeyedMutator, type SWRConfiguration, type SWRResponse } from "swr";

import ServiceStackClientContext from "src/contexts/api/ServiceStackClientContext";

import useSwrApi, { type IHttpVerb } from "./useSwrApi";

/**
 * Defines optional response projections; additional state is merged after standard API fields.
 */
export interface IApiConfiguration<T, D, E, A> {
    getData?: (response?: SWRResponse<T, T>) => D | undefined;
    getError?: (response?: SWRResponse<T, T>) => E | undefined;
    getAdditionalState?: (response?: SWRResponse<T, T>) => A | undefined;
}

/**
 * Projects SWR response state through optional data, error and additional-state selectors.
 *
 * Postconditions: Loading requires validation with falsy projected data and error. Additional
 * state is spread last and can override standard fields.
 *
 * Assumptions: Existing memo dependencies are retained; changing only the additional-state
 * selector does not invalidate this projection.
 */
export const useApi = <T, D, E, A>(
    httpVerb: IHttpVerb,
    fn?: () => IReturn<T>,
    getConfiguration?: IApiConfiguration<T, D, E, A>,
    swrConfiguration?: SWRConfiguration | undefined
) => {
    const response = useSwrApi<T>(httpVerb, fn, swrConfiguration);
    const client = useContext(ServiceStackClientContext);

    const { getData, getError, getAdditionalState } = getConfiguration ?? {};

    const result: {
        data: D | undefined;
        error: E | undefined;
        loading: boolean;
        validating: boolean;
        mutate: KeyedMutator<T>;
        refresh: (options?: { strict?: boolean }) => Promise<T>;
    } & A = useMemo(() => {
        const d = getData?.(response);
        const e = getError?.(response);
        const a = getAdditionalState?.(response);

        const s = {
            data: d,
            error: e,
            loading: response.isValidating && !d && !e,
            validating: response.isValidating,
            mutate: response.mutate,
            /**
             * Requests ordinary cached revalidation unless strict refresh is selected.
             * Strict refresh fetches before replacing cached data and propagates failures;
             * existing data remains available while that request runs or fails.
             */
            refresh: async (options?: { strict?: boolean }) => {
                if (!options?.strict) {
                    return response.mutate(response.data, { revalidate: true });
                }
                const request = fn?.();
                if (!request) {
                    return response.data;
                }
                const newResponse = await client[httpVerb](request);
                const status = (newResponse as { responseStatus?: { errorCode?: string } })
                    ?.responseStatus;
                if (status?.errorCode) {
                    throw newResponse;
                }
                await response.mutate(newResponse, { revalidate: false });
                return newResponse;
            }
        };

        return {
            ...s,
            ...a
        } as typeof s & A;
    }, [
        getData,
        getError,
        response.data,
        response.error,
        response.isLoading,
        response.isValidating,
        response.mutate,
        client,
        fn,
        httpVerb
    ]);

    return result;
};

export default useApi;
